import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { codeConfirmationKey, selectSubscriptionCodeConfirmation } from 'app/lib/code-confirmation';
import React, { useEffect, useEffectEvent, useRef, useState, type JSX } from 'react';
import {
  PixelRatio,
  Platform,
  StyleSheet,
  View,
  FlatList,
  type ColorValue,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Button } from 'app/design/primitives/button';
import { Trans } from 'react-i18next';

import { Box, Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import Animated from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useOnePreference, useUpsertOnePreference } from 'app/network/preferences';
import { TrialModalType } from 'app/types/preferences';
import type { NavigationScreen } from 'app/types/navigation';
import { withErrorBoundary } from 'app/components/error-boundary';
import Constants from 'expo-constants';
import { Event, track, trackOnFacebook } from 'app/lib/analytics';
import { useAllAddresses } from 'app/network/address';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useUserCanBeInsured, useUserCurrency, useUserIsInsured, useUserMetaPrompt } from 'app/network/user';
import { isFreeTrialPlanCode, signedUpRecently, useMyActivePlans } from 'app/network/plan';
import IntroPerson from 'app/design/images/intro/person';
import IntroInsured from 'app/design/images/intro/insured';
import IntroZeroOnePercent from 'app/design/images/intro/zero-one-percent';
import IntroCleaner from 'app/design/images/intro/cleaner';
import IntroPig from 'app/design/images/intro/pig';
import IntroBorrow from 'app/design/images/intro/borrow';
import { type FreeSubSignalData, getSignalData, type ResponseWithSignals, Signal } from 'app/network/signals';
import IntroPromo from 'app/design/images/intro/promo';
import { useMyProfile } from 'app/network/profile';
import IntroCommunity from 'app/design/images/intro/community';
import { StorageKey } from 'app/lib/storageKeys';
import { useStoredValue } from 'app/lib/storage';
import { useReferralPrizeAvailable, useTrialFreeSubAvailable } from 'app/network/promotions';
import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import {
  codeFullWarningAtom,
  showIntroAfterSubscriptionCodeModalAtom,
  isLoggedInAtom,
  pauseModalsAtom,
  presentB2BActivatedModalAtom,
  presentB2CPaidModalAtom,
  presentSubscriptionCodeModalAtom,
  selectedCodeConfirmationAtom,
  userInfoAtom,
  presentUserMetaModalAtom,
  hasProfileAtom,
  isOnboardingAtom,
  babillardReadyAtom,
  alreadyRedirectedOnAppStartAtom,
} from 'app/atoms';
import { useNavigation, useNavigationOptions } from 'app/hooks/navigation';
import { CONTACT_EMAIL } from 'app/../app.config.json';
import IntroRefrain1 from 'app/design/images/intro/refrain1';
import IntroRefrain2 from 'app/design/images/intro/refrain2';
import IntroRefrain3 from 'app/design/images/intro/refrain3';
import { WhiteLabel } from 'app/types/white-labels';
import { useAddOneNotification } from 'app/network/notification';
import { NotificationTemplateIdentifier } from 'app/types/notification';
import { useTrackNewListingSource } from 'app/network/listing';
import { AppEventsLogger } from 'react-native-fbsdk-next';
import IntroSupport from 'app/design/images/intro/support';
import { Enveloppe } from 'app/design/icons/enveloppe';
import { ChatBubble } from 'app/design/icons/chat-bubble';
import { MODAL_WIDTH, safelyOpenURL } from 'app/lib/navigation';
import IntroTessier1 from 'app/design/images/intro/tessier1';
import IntroTessier2 from 'app/design/images/intro/tessier2';
import IntroTessier3 from 'app/design/images/intro/tessier3';
import { groupNameWithPrefix } from 'app/network/groups';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { navigateToIntro, navigateToLegacyQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { getI18nLanguage } from 'app/lib/language';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IntroCarouselDots } from './intro-carousel-dots';
import { IntroModalActions } from './intro-modal-actions';
const theme = createDSTheme();

export interface IntroModalItem {
  id: string;
  hero: JSX.Element;
  image: JSX.Element;
  bgColor: string;
  textColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  values?: Record<string, string | undefined>;
}

export const MODAL_COLORS = {
  Green: '#B8ECE0',
  Purple: '#A0A7FF',
  Pink: '#FFA9AB',
  Yellow: '#EDD441',
  Orange: '#FFAD54',
};

const AnimatedFlatList = Animated.FlatList;

// If you add a new 'use' here, maybe add a new value near createNotification()..?
export type IntroModalUse = 'intro' | 'b2c_paid' | 'b2b_activated' | 'newly_insured' | 'offre_defricheur' | 'custom';

export interface IntroModalParams {
  freeSubAvailable: boolean | 'true' | 'false' | FreeSubSignalData['program'];
  use: IntroModalUse;
  from?: 'notif';
  customTemplate?: string;
}

const normalizeIntroModalParams = (params: IntroModalParams): IntroModalParams => {
  if (params.freeSubAvailable === 'true') return { ...params, freeSubAvailable: true };
  if (params.freeSubAvailable === 'false') return { ...params, freeSubAvailable: false };
  return params;
};

const getIntroModalLayout = ({
  deviceWidth,
  isLargeUI,
  isSmallUI,
}: {
  deviceWidth: number;
  isLargeUI: boolean;
  isSmallUI: boolean;
}) => {
  const itemMargin = 0;
  const overflow = 0;
  const w = (isSmallUI ? deviceWidth : MODAL_WIDTH) - 2 * itemMargin;
  const itemWidth = w / (1 + overflow);
  const isTablet = w > 550;
  const pixelRatioScale = isLargeUI ? 1.2 : isTablet ? 1 : Math.max(0.8, PixelRatio.get() / 3);
  return { imageSize: itemWidth - 60, itemMargin, itemWidth, pixelRatioScale, startScroll: 0, w };
};

const useSelectedCarouselItem = (carouselItems: IntroModalItem[]) => {
  const carouselKey = carouselItems.map((item) => item.id).join('|');
  const [state, setState] = useState<{ source: string; id: IntroModalItem['id'] | undefined }>(() => ({
    source: carouselKey,
    id: carouselItems[0]?.id,
  }));
  let currentState = state;
  if (state.source !== carouselKey) {
    currentState = { source: carouselKey, id: carouselItems[0]?.id };
    setState(currentState);
  }
  const selectedTab = carouselItems.find((item) => item.id === currentState.id) ?? carouselItems[0];
  return {
    selectedTab,
    setSelectedTab: (item: IntroModalItem) => setState({ source: carouselKey, id: item.id }),
  };
};

interface IntroModalItemsOptions {
  codeFullWarning: unknown;
  freeSubAvailable: IntroModalParams['freeSubAvailable'];
  groupName: string | undefined;
  groupSubtype: string | undefined;
  i18nLanguage: string;
  imageSize: number;
  isB2B: boolean;
  isOnFreeTrial: boolean;
  params: IntroModalParams;
  pixelRatioScale: number;
  referralPrizeAvailable: boolean;
  userCanBeInsured: boolean;
  userCurrency: ReturnType<typeof useUserCurrency>;
  userIsInsured: ReturnType<typeof useUserIsInsured>;
  w: number;
}

function buildIntroModalItems({
  codeFullWarning,
  freeSubAvailable,
  groupName,
  groupSubtype,
  i18nLanguage,
  imageSize,
  isB2B,
  isOnFreeTrial,
  params,
  referralPrizeAvailable,
  userCanBeInsured,
  userCurrency,
  userIsInsured,
  t,
}: IntroModalItemsOptions & { t: (key: string) => string }): Omit<IntroModalItem, 'hero'>[] {
  const items: Omit<IntroModalItem, 'hero'>[] = [];

  if (params.use === 'intro') {
    if (isWhiteLabel(WhiteLabel.Refrain)) {
      items.push({
        id: 'first',
        bgColor: '#6A3045',
        textColor: '#EDF5FF',
        image: <IntroRefrain1 height={(imageSize * 216) / 317} width={imageSize} />,
      });
      items.push({
        id: 'second',
        bgColor: '#E7AA3C',
        textColor: '#6A3045',
        image: <IntroRefrain2 height={(imageSize * 216) / 317} width={imageSize} />,
      });
      items.push({
        id: 'third',
        bgColor: '#5F9B7B',
        textColor: '#EDF5FF',
        image: <IntroRefrain3 height={(imageSize * 216) / 317} width={imageSize} />,
      });
    } else if (isWhiteLabel(WhiteLabel.SerresTessier)) {
      items.push({
        id: 'first',
        bgColor: '#244E1B',
        textColor: '#FFFFFF',
        buttonColor: '#E2E24B',
        buttonTextColor: '#000000',
        image: <IntroTessier1 height={(imageSize * 216) / 317} width={imageSize} />,
      });
      items.push({
        id: 'second',
        bgColor: '#5B220B',
        textColor: '#FFFFFF',
        buttonColor: '#EE7B30',
        buttonTextColor: '#000000',
        image: <IntroTessier2 height={(imageSize * 216) / 317} width={imageSize} />,
      });
      items.push({
        id: 'third',
        bgColor: '#163E6F',
        textColor: '#FFFFFF',
        buttonColor: '#7CCCF2',
        buttonTextColor: '#000000',
        image: <IntroTessier3 height={(imageSize * 216) / 317} width={imageSize} />,
      });
    } else {
      // Partage Club, and white labels without a dedicated carousel (e.g. Mutuo) — use the same slides as core

      if (codeFullWarning) {
        items.push({
          id: 'codeFullWarning',
          values: { groupName: groupNameWithPrefix(codeFullWarning as string, i18nLanguage) },
          bgColor: MODAL_COLORS.Pink,
          image: <IntroCommunity height={(imageSize * 216) / 317} width={imageSize} />,
        });
      }

      items.push({
        id: `welcome.${isOnFreeTrial ? `trial.${userCurrency}` : isB2B ? 'b2b' : 'regular'}`,
        values: { groupName, withWho: groupSubtype ? t(`welcome.withWho.${groupSubtype}`) : undefined },
        bgColor: MODAL_COLORS.Green,
        image: isB2B ? (
          <IntroPromo height={(imageSize * 216) / 317} width={imageSize} />
        ) : (
          <IntroPerson height={(imageSize * 216) / 317} width={imageSize} />
        ),
      });

      if ((isOnFreeTrial && userCanBeInsured) || userIsInsured) {
        items.push({
          id: `insurable.insurance.${isOnFreeTrial ? 'trial' : 'regular'}`,
          bgColor: MODAL_COLORS.Purple,
          image: <IntroInsured height={(imageSize * 216) / 317} width={imageSize} />,
        });
        items.push({
          id: 'insurable.zeroOnePercent',
          bgColor: MODAL_COLORS.Pink,
          image: <IntroZeroOnePercent height={(imageSize * 216) / 317} width={imageSize} />,
        });
      } else {
        // Insurance not available
        items.push({
          id: 'notInsurable.savings',
          bgColor: MODAL_COLORS.Purple,
          image: <IntroPig height={(imageSize * 216) / 317} width={imageSize} />,
        });
        items.push({
          id: 'notInsurable.community',
          bgColor: MODAL_COLORS.Pink,
          image: <IntroCommunity height={(imageSize * 216) / 317} width={imageSize} />,
        });
      }

      items.push({
        id: 'cleaner',
        bgColor: MODAL_COLORS.Yellow,
        image: <IntroCleaner height={(imageSize * 216) / 317} width={imageSize} />,
      });

      if (freeSubAvailable === 'defricheur') {
        items.push({
          id: 'offreDefricheur',
          bgColor: MODAL_COLORS.Green,
          image: <IntroPromo height={(imageSize * 216) / 317} width={imageSize} />,
        });
      }
    }
  }

  if (params.use === 'newly_insured') {
    items.push({
      id: 'insurable.insurance.regular',
      bgColor: MODAL_COLORS.Purple,
      image: <IntroInsured height={(imageSize * 216) / 317} width={imageSize} />,
    });
  }

  if (params.use === 'offre_defricheur') {
    items.push({
      id: 'offreDefricheur',
      bgColor: MODAL_COLORS.Green,
      image: <IntroPromo height={(imageSize * 216) / 317} width={imageSize} />,
    });
  }

  if (params.use === 'b2c_paid' || params.use === 'b2b_activated') {
    items.push({
      id: `welcome.${params.use === 'b2c_paid' ? 'regular' : 'b2b'}`,
      values: { groupName, withWho: groupSubtype ? t(`welcome.withWho.${groupSubtype}`) : undefined },
      bgColor: MODAL_COLORS.Green,
      image:
        params.use === 'b2b_activated' ? (
          <IntroPromo height={(imageSize * 216) / 317} width={imageSize} />
        ) : (
          <IntroPerson height={(imageSize * 216) / 317} width={imageSize} />
        ),
    });
    items.push({
      id: 'borrow',
      bgColor: MODAL_COLORS.Pink,
      image: <IntroBorrow height={(imageSize * 216) / 317} width={imageSize} />,
    });
    if (userIsInsured) {
      items.push({
        id: 'insurable.insurance.regular',
        bgColor: MODAL_COLORS.Purple,
        image: <IntroInsured height={(imageSize * 216) / 317} width={imageSize} />,
      });
    }
    if (referralPrizeAvailable) {
      items.push({
        id: 'referralPromo',
        bgColor: MODAL_COLORS.Yellow,
        image: <IntroPromo height={(imageSize * 216) / 317} width={imageSize} />,
      });
    }
  }

  if (params.use === 'custom') {
    items.push({
      id: `custom.${params.customTemplate!}`,
      bgColor: MODAL_COLORS.Orange,
      image: <IntroSupport height={(imageSize * 216) / 317} width={imageSize} />,
    });
  }

  return items;
}

function useIntroModalItems(options: IntroModalItemsOptions) {
  const { t } = useScopedTranslation();
  const { pixelRatioScale, w } = options;
  const heroStrongStyle = {
    fontSize: 19 * pixelRatioScale,
    lineHeight: 27 * pixelRatioScale,
    textAlign: 'center' as const,
  };
  const items = buildIntroModalItems({ ...options, t });

  if (!items.length) {
    return [];
  }

  return items.map((item) => ({
    ...item,
    hero: (
      <Trans
        components={{
          strong: (
            <Text
              fontWeight="700"
              style={{ ...heroStrongStyle, color: item.textColor ?? theme.colors['text.primary'] }}
            />
          ),
          underline: (
            <Text
              style={{
                color: item.textColor ?? theme.colors['text.primary'],
                textDecorationLine: 'underline',
              }}
            />
          ),
          smaller: (
            <Text
              style={{
                color: item.textColor ?? theme.colors['text.primary'],
                fontSize: 19 * pixelRatioScale,
                lineHeight: 23 * pixelRatioScale,
              }}
            />
          ),
          padding: <View style={{ height: 8, width: w }} />,
        }}
        i18nKey={item.id}
        ns="introModal"
        values={item.values}
      />
    ),
  }));
}

type IntroModalScreenProps = React.ComponentProps<NavigationScreen<'introModal'>>;

const useIntroModalScreenModel = ({ navigation, route }: IntroModalScreenProps) => {
  const { featureFlags } = useListingCatalog();
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const params = normalizeIntroModalParams(route.params);

  const setPageTitle = usePageTitle('introModal');
  useEffect(() => setPageTitle(`pageTitle.${params.use}`), [params.use, setPageTitle]);

  const { data: profile } = useMyProfile();
  const { data: addresses } = useAllAddresses();
  const firstAddress = addresses?.[0];
  const userIsInsured = useUserIsInsured();
  const userCanBeInsured = useUserCanBeInsured();
  const referralPrizeAvailable = useReferralPrizeAvailable();
  const userCurrency = useUserCurrency();

  const { data: userPlan } = useMyActivePlans({ throwOnError: true });
  const planCode = userPlan?.[0]?.code.code;
  const isOnFreeTrial = isFreeTrialPlanCode(planCode);
  const isB2B = Boolean(userPlan?.[0]?.code.group);
  const groupName = groupNameWithPrefix(userPlan?.[0]?.code.group?.name, i18nLanguage);
  const groupSubtype = userPlan?.[0]?.code.group?.subtype;

  const freeSubAvailable = params.freeSubAvailable;

  const codeFullWarning = useAtomValue(codeFullWarningAtom);

  useEffect(() => {
    if (freeSubAvailable === 'defricheur' && firstAddress) {
      track(Event.OffreDefricheurOpened, { address: firstAddress });
    } else if (freeSubAvailable === 'trial') {
      track(Event.OffreTrialOpened);
    }
  }, [freeSubAvailable, firstAddress]);

  const userModalPreference = useOnePreference('trialModal').data?.value;
  const { mutate: updateModalPreference } = useUpsertOnePreference('trialModal');

  const { isSmallUI, isLargeUI } = useUISize();
  const deviceWidth = useDeviceWidth();
  const { imageSize, itemMargin, itemWidth, pixelRatioScale, startScroll, w } = getIntroModalLayout({
    deviceWidth,
    isLargeUI,
    isSmallUI,
  });

  const heroStyles = StyleSheet.create({
    hero: {
      width: itemWidth,
      alignSelf: 'center',
      fontSize: 18 * pixelRatioScale,
      lineHeight: 26 * pixelRatioScale,
      maxWidth: 520,
      paddingHorizontal: 32 * pixelRatioScale,
      paddingBottom: theme.spacing['spacing.general.x4'] * pixelRatioScale,
      textAlign: 'center',
    },
    heroStrong: {
      fontSize: 19 * pixelRatioScale,
      lineHeight: 27 * pixelRatioScale,
      textAlign: 'center',
    },
    dotsContainer: {
      alignContent: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    dot: {
      width: PixelRatio.roundToNearestPixel(11),
      height: PixelRatio.roundToNearestPixel(11),
      marginHorizontal: 4,
      marginVertical: 4,
      borderRadius: 5.5,
      backgroundColor: '#ffffff',
    },
    dotActive: {
      backgroundColor: theme.colors['text.primary'],
      width: PixelRatio.roundToNearestPixel(32),
    },
  });

  useNavigationOptions(navigation, {
    headerShown: false,
  });

  const gotoQuickOffersNext = params.use === 'intro' || params.use === 'offre_defricheur';

  const carouselItems = useIntroModalItems({
    codeFullWarning,
    freeSubAvailable,
    groupName,
    groupSubtype,
    i18nLanguage,
    imageSize,
    isB2B,
    isOnFreeTrial,
    params,
    pixelRatioScale,
    referralPrizeAvailable,
    userCanBeInsured,
    userCurrency,
    userIsInsured,
    w,
  });

  const snapToOffsets = carouselItems.map((_, i) => startScroll + i * itemWidth);

  const { selectedTab, setSelectedTab } = useSelectedCarouselItem(carouselItems);
  const activeTab = selectedTab;
  const bgColor = activeTab?.bgColor ?? '#F0DDE0';

  const flatListRef = useRef<FlatList>(null);

  const setIndex = (index: number) => {
    if (carouselItems.length === 0) return;
    const i = Math.max(0, Math.min(index, carouselItems.length - 1));
    const item = carouselItems[i];
    if (!item) return;
    setSelectedTab(item);
  };

  const selectedTabForAnalytics = useRef(selectedTab);

  const trackTab = () => {
    if (!selectedTab) return;
    if (selectedTab === selectedTabForAnalytics.current) {
      return;
    }
    selectedTabForAnalytics.current = selectedTab;
    track(Event.ChangeTab, {
      page_name: 'introModal',
      tab: selectedTab.id,
    });
  };

  const scrollHandler = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (carouselItems.length === 0) return;
    const raw = Math.abs(Math.round(e.nativeEvent.contentOffset.x / itemWidth));
    const index = Math.max(0, Math.min(raw, carouselItems.length - 1));
    scheduleOnRN(setIndex, index);
  };

  const finishedIntro = (finished: boolean) => {
    if (params.use === 'intro' && userModalPreference === TrialModalType.once) {
      updateModalPreference(TrialModalType.never);
      if (isSmallUI) {
        trackOnFacebook(AppEventsLogger.AppEvents.CompletedTutorial, {
          [AppEventsLogger.AppEventParams.Success]: finished ? 'true' : 'false',
        });
      }
    }
  };

  const closeScreen = () => {
    finishedIntro(false);
    navigation.goBack();
  };

  const trackNewListingSource = useTrackNewListingSource();

  const pressContinue = () => {
    if (!activeTab) return;
    const index = Math.max(0, carouselItems.indexOf(activeTab));
    if (index >= carouselItems.length - 1) {
      if (gotoQuickOffersNext && isSmallUI) {
        finishedIntro(true);
        trackNewListingSource('intro');
        navigation.goBack();
        setTimeout(() => {
          navigateToLegacyQuickPublishFlow(navigation, { featureFlags });
        }, 100);
      } else {
        closeScreen();
      }
      return;
    }
    // setSelectedTab(carouselItems[index + 1]!);
    flatListRef.current?.scrollToIndex({
      index: index + 1,
      animated: true,
    });
  };

  const pressPrevious = () => {
    if (!activeTab) return;
    const index = Math.max(0, carouselItems.indexOf(activeTab));
    flatListRef.current?.scrollToIndex({
      index: index - 1,
      animated: true,
    });
  };

  const pressSupport = (type: 'email' | 'in-app') => {
    closeScreen();
    if (type === 'in-app') {
      navigateToChat(navigation, SUPPORT_CHANNEL_URL);
    } else {
      void safelyOpenURL(`mailto:${CONTACT_EMAIL}`);
    }
  };

  const titleColor: ColorValue = activeTab?.textColor ?? theme.colors['text.primary'];

  useEffect(() => {
    if (carouselItems.length === 0 || !selectedTab) return;
    const raw = carouselItems.indexOf(selectedTab);
    const index = raw === -1 ? 0 : Math.max(0, Math.min(raw, carouselItems.length - 1));
    const offset = snapToOffsets[Math.max(0, Math.min(index, snapToOffsets.length - 1))] ?? 0;
    flatListRef.current?.scrollToOffset({
      offset,
      animated: true,
    });
  }, [selectedTab, snapToOffsets, carouselItems]);

  const { mutate: createNotification } = useAddOneNotification();
  useEffect(() => {
    if (!profile) return;
    if (params.from === 'notif') return;
    if (!['intro', 'b2c_paid', 'b2b_activated', 'newly_insured'].includes(params.use)) return;
    createNotification({
      subscriberId: profile.userId,
      type: NotificationTemplateIdentifier.generic,
      title: `introModal.${params.use}.title`,
      message: `introModal.${params.use}.body`,
      messageArgs: { groupName: groupName ?? '' },
      link: `/intro/introModal?use=${params.use}&freeSubAvailable=${params.freeSubAvailable.toString()}&from=notif`,
    });
  }, [createNotification, groupName, params.freeSubAvailable, params.from, params.use, profile]);

  function renderHeroItem(item: IntroModalItem) {
    return (
      <View
        style={{
          width: itemWidth,
          paddingVertical: 20,
        }}
      >
        <Box alignItems="center" style={{ flex: 1 }}>
          <Box>{item.image}</Box>
          <Text
            style={{ ...heroStyles.hero, color: item.textColor ?? theme.colors['text.primary'] }}
            variant="bodyBase"
          >
            {item.hero}
          </Text>
          {item.id === 'custom.support' && (
            <Row
              gap="spacing.general.x4"
              justifyContent="space-evenly"
              style={{
                marginTop: theme.spacing['spacing.general.x2'],
                paddingHorizontal: heroStyles.hero.paddingHorizontal,
              }}
            >
              <Button
                onPress={() => pressSupport('in-app')}
                style={{ height: 40, flexGrow: 1, flexBasis: 0, borderWidth: 2 }}
                variant="secondary"
              >
                <Row gap="spacing.general.x1">
                  <Text fontWeight="700" lineHeight={24} variant="bodyBase">
                    {t('custom.supportButtons.inAppMessaging')}
                  </Text>
                  <ChatBubble height={24} width={24} />
                </Row>
              </Button>
              <Button
                onPress={() => pressSupport('email')}
                style={{ height: 40, flexGrow: 1, flexBasis: 0, borderWidth: 2 }}
                variant="secondary"
              >
                <Row gap="spacing.general.x1">
                  <Text fontWeight="700" lineHeight={24} variant="bodyBase">
                    {t('custom.supportButtons.email')}
                  </Text>
                  <Enveloppe height={24} width={24} />
                </Row>
              </Button>
            </Row>
          )}
        </Box>
      </View>
    );
  }

  if (carouselItems.length === 0) {
    navigation.goBack();
    return null;
  }

  return {
    activeTab,
    bgColor,
    carouselItems,
    closeScreen,
    deviceWidth,
    flatListRef,
    gotoQuickOffersNext,
    heroStyles,
    isLargeUI,
    isSmallUI,
    itemMargin,
    params,
    pixelRatioScale,
    pressContinue,
    pressPrevious,
    profile,
    renderHeroItem,
    scrollHandler,
    snapToOffsets,
    t,
    titleColor,
    trackTab,
    w,
  };
};

function IntroModalScreenView({
  activeTab,
  bgColor,
  carouselItems,
  closeScreen,
  deviceWidth,
  flatListRef,
  gotoQuickOffersNext,
  heroStyles,
  isLargeUI,
  isSmallUI,
  itemMargin,
  params,
  pixelRatioScale,
  pressContinue,
  pressPrevious,
  profile,
  renderHeroItem,
  scrollHandler,
  snapToOffsets,
  t,
  titleColor,
  trackTab,
  w,
}: NonNullable<ReturnType<typeof useIntroModalScreenModel>>) {
  return (
    <Column
      testID="intro-modal"
      style={{
        paddingTop: Constants.statusBarHeight,
        backgroundColor: bgColor,
        width: isSmallUI ? deviceWidth : '100%',
        height: '100%',
        paddingBottom: isSmallUI ? 0 : theme.spacing['spacing.general.x10'],
        alignItems: 'center',
      }}
    >
      <Row alignItems="center" style={{ height: 50, alignSelf: 'flex-start' }}>
        <Button flex={1} onPress={closeScreen} style={{ maxWidth: 50 }}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
        </Button>
      </Row>

      <Text
        flex={0}
        style={{
          color: titleColor,
          fontSize: 28,
          fontWeight: '700',
          lineHeight: 34,
          marginHorizontal: 60,
          marginTop: theme.spacing['spacing.general.x2'],
          alignSelf: 'flex-start',
        }}
        variant="bodyBase"
      >
        {t(params.use === 'newly_insured' ? 'titleNewFeature' : 'title', { name: profile?.firstName })}
      </Text>

      <AnimatedFlatList
        bounces
        data={carouselItems}
        decelerationRate={Platform.OS === 'ios' ? 'fast' : 'normal'}
        disableIntervalMomentum
        horizontal
        keyExtractor={(item, _) => item.id}
        onMomentumScrollEnd={trackTab}
        onScroll={scrollHandler}
        // pagingEnabled
        ref={flatListRef}
        renderItem={({ item }) => renderHeroItem(item)}
        scrollEventThrottle={100}
        showsHorizontalScrollIndicator={false}
        snapToOffsets={snapToOffsets}
        style={{
          flex: 2,
          overflow: isSmallUI ? 'visible' : 'scroll',
          width: w,
          marginLeft: itemMargin,
        }}
      />

      <IntroCarouselDots
        {...{ activeTab, carouselItems, isSmallUI }}
        dotActiveStyle={heroStyles.dotActive}
        dotStyle={heroStyles.dot}
      />
      <IntroModalActions
        {...{ activeTab, carouselItems, gotoQuickOffersNext, isLargeUI, pixelRatioScale, t }}
        onContinue={pressContinue}
        onPrevious={pressPrevious}
      />
    </Column>
  );
}

const IntroModalScreenComponent: NavigationScreen<'introModal'> = (props) => {
  const model = useIntroModalScreenModel(props);
  return model ? <IntroModalScreenView {...model} /> : null;
};

export const IntroModalScreen: NavigationScreen<'introModal'> = withErrorBoundary(IntroModalScreenComponent);

function canPresentFeatureAfterStartup(
  prerequisites: readonly unknown[],
  loadedValues: readonly unknown[],
  blockers: readonly unknown[],
  modals: readonly { waitFor: () => unknown; showIf: () => unknown }[],
) {
  return (
    prerequisites.every(Boolean) &&
    loadedValues.every((value) => value !== undefined) &&
    !blockers.some(Boolean) &&
    !modals.some((modal) => modal.waitFor() && Boolean(modal.showIf()))
  );
}

export function useAppStartModals() {
  const navigation = useNavigation();

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userIsInsured = useUserIsInsured();
  const { data: activePlans, isSuccess: plansLoaded } = useMyActivePlans();
  const userPlan = activePlans?.[0];
  const { freeSubAvailable } = useTrialFreeSubAvailable()();
  const userInfo = useAtomValue(userInfoAtom);

  const [newFeatureModalShown, setNewFeatureModalShown] = useStoredValue<boolean | null>(
    StorageKey.NewlyInsuredModalShown,
    null,
  );
  const userTrialModalPreference = useOnePreference('trialModal').data?.value;
  const [numAppStarts] = useStoredValue<number>(StorageKey.NumAppStarts, 0);
  const [customModalsShown, setCustomModalsShown] = useStoredValue<string[]>(StorageKey.CustomModalsShown, []);
  const [b2BActivatedModalShown, setB2BActivatedModalShown] = useStoredValue<boolean | null>(
    StorageKey.B2BActivatedModalShown,
    null,
  );
  const { mutate: createNotification } = useAddOneNotification();
  const { data: profile } = useMyProfile();
  // const [closestOffersModalShown, setClosestOffersModalShown] = useStoredValue<boolean | null>(
  //   StorageKey.ClosestOffersModalShown,
  //   null,
  // );
  // const { data: closestOffersCategories } = useClosestOffersCategories();

  const [codeConfirmationsShown, setCodeConfirmationsShown] = useStoredValue<string[] | null>(
    StorageKey.CodeConfirmationsShown,
    null,
  );
  const setSelectedCodeConfirmation = useSetAtom(selectedCodeConfirmationAtom);
  const confirmationStateLoaded = codeConfirmationsShown !== undefined && userInfo !== null;
  const pendingConfirmation = selectSubscriptionCodeConfirmation(
    userInfo?.codeConfirmationPrompts ?? [],
    codeConfirmationsShown,
    userInfo?.id,
    userPlan?.code.code,
  );
  const [subscriptionPromptStarted, setSubscriptionPromptStarted] = useState(false);

  const [chooseSharerTypeModalShown, setChooseSharerTypeModalShown] = useStoredValue<boolean | null>(
    StorageKey.ChooseSharerTypeModalShown,
    null,
  );

  const [presentB2CPaidModal, setPresentB2CPaidModal] = useAtom(presentB2CPaidModalAtom);
  const presentB2BActivatedModal = useAtomValue(presentB2BActivatedModalAtom);
  const pausedModals = useAtomValue(pauseModalsAtom);
  const [isSubscriptionCodeModalOpen, setIsSubscriptionCodeModalOpen] = useAtom(presentSubscriptionCodeModalAtom);
  const [showIntroAfterSubscriptionCodeModal, setShowIntroAfterSubscriptionCodeModal] = useAtom(
    showIntroAfterSubscriptionCodeModalAtom,
  );

  const [userMetaModalShown, setUserMetaModalShown] = useState<boolean>(false);
  const [isUserMetaModalOpen, setIsUserMetaModalOpen] = useAtom(presentUserMetaModalAtom);
  const userMetaPrompt = useUserMetaPrompt();

  const userSharerType = useOnePreference('sharerType').data;
  const hasProfile = useAtomValue(hasProfileAtom);
  const isOnboarding = useAtomValue(isOnboardingAtom);
  const isBabillardReady = useAtomValue(babillardReadyAtom);
  const [alreadyRedirected, setAlreadyRedirected] = useAtom(alreadyRedirectedOnAppStartAtom);

  const modals = [
    {
      // Both offers are selected from address eligibility; neither has a special priority.
      name: 'subscription_code_confirmation',
      waitFor: () => Boolean(userPlan) && confirmationStateLoaded,
      showIf: () => Boolean(pendingConfirmation && hasProfile && !isOnboarding),
      onShow: (_: string) => {
        if (!pendingConfirmation || !userInfo?.id) return;
        setSubscriptionPromptStarted(true);
        setSelectedCodeConfirmation({ userId: userInfo.id, prompt: pendingConfirmation.prompt });
        setCodeConfirmationsShown([
          ...(codeConfirmationsShown ?? []),
          codeConfirmationKey(userInfo.id, pendingConfirmation.prompt),
        ]);
        track(Event.SubscriptionCodePopupShown, { code: pendingConfirmation.code });
        setIsSubscriptionCodeModalOpen(true);
      },
      deps: [
        subscriptionPromptStarted,
        pendingConfirmation,
        codeConfirmationsShown,
        setCodeConfirmationsShown,
        setSelectedCodeConfirmation,
        setIsSubscriptionCodeModalOpen,
        hasProfile,
        isOnboarding,
      ],
    },
    {
      // Ask the new member a question, using server-side-defined prompts
      name: 'group_meta_prompt',
      waitFor: () => Boolean(userInfo && isLoggedIn),
      showIf: () => Boolean(!userMetaModalShown && userMetaPrompt) && !isUserMetaModalOpen,
      onShow: (_: string) => {
        setIsUserMetaModalOpen(true);
        setUserMetaModalShown(true);
      },
      deps: [
        isLoggedIn,
        isUserMetaModalOpen,
        setIsUserMetaModalOpen,
        setUserMetaModalShown,
        userMetaModalShown,
        userMetaPrompt,
      ],
    },
    {
      // First popup: intro after initial sign-up
      name: 'intro-after-signup',
      waitFor: () =>
        Boolean(
          userInfo &&
          isLoggedIn &&
          freeSubAvailable !== undefined &&
          userPlan &&
          userTrialModalPreference &&
          userTrialModalPreference !== TrialModalType.never,
        ),
      showIf: () => {
        const conds = {
          signedUpRecently: signedUpRecently(userPlan),
          notPublic: userInfo?.type !== 'public',
          confirmationStateLoaded,
          notSubscriptionCodeOpen: !isSubscriptionCodeModalOpen,
          confirmationResolved: subscriptionPromptStarted || !pendingConfirmation,
          notUserMetaOpen: !isUserMetaModalOpen,
          hasProfile,
          notOnboarding: !isOnboarding,
          isBabillardReady,
        };
        return Object.values(conds).every(Boolean);
      },
      onShow: (_: string) => {
        setNewFeatureModalShown(true); // New feature (newly_insured) modal is not needed, since the intro includes it
        setTimeout(() => {
          if (isFeatureEnabled(FeatureFlag.useNewOnboarding)) {
            navigation.navigate('presentCommunity', undefined, { pop: true });
          } else {
            navigateToIntro(navigation, freeSubAvailable!, 'intro');
          }
        }, 1000);
        setShowIntroAfterSubscriptionCodeModal(false); // Reset the atom so that this modal doesn't show again
      },
      showEvenIfOtherModalsTriggered: true,
      deps: [
        freeSubAvailable,
        isLoggedIn,
        navigation,
        userPlan,
        userTrialModalPreference,
        showIntroAfterSubscriptionCodeModal,
        isSubscriptionCodeModalOpen,
        subscriptionPromptStarted,
        pendingConfirmation,
        codeConfirmationsShown,
        setCodeConfirmationsShown,
        setSelectedCodeConfirmation,
        hasProfile,
        isOnboarding,
        isUserMetaModalOpen,
        isBabillardReady,
      ],
    },
    {
      // Popup when B2C activated (paid for) a license
      name: 'b2c_paid',
      waitFor: () => isLoggedIn,
      showIf: () => presentB2CPaidModal,
      showEvenIfOtherModalsTriggered: true, // Needed because the user can pay during their first session, and we need this popup to show ASAP when a B2C license was activated
      onShow: (_: string) => {
        setPresentB2CPaidModal(false);
        if (isFeatureEnabled(FeatureFlag.useNewOnboarding)) {
          navigation.navigate('payment-success');
        } else {
          navigateToIntro(navigation, false, 'b2c_paid');
        }
      },
      deps: [navigation, presentB2CPaidModal, setPresentB2CPaidModal],
    },
    {
      // Popup when B2B activation code is used
      name: 'b2b_activated',
      waitFor: () => isLoggedIn,
      showIf: () => Boolean(presentB2BActivatedModal && !b2BActivatedModalShown && userPlan?.code.group?.name),
      showEvenIfOtherModalsTriggered: true, // Needed because the user can activate during their first session, and we need this popup to show ASAP when B2B was activated
      onShow: (_: string) => {
        if (isFeatureEnabled(FeatureFlag.useNewOnboarding)) {
          navigation.navigate('presentCommunity');
        } else {
          navigateToIntro(navigation, false, 'b2b_activated');
        }
        setB2BActivatedModalShown(true); // Set the atom so that this modal doesn't show again
      },
      deps: [navigation, presentB2BActivatedModal],
    },
    {
      // Second popup: "newly insured"
      name: 'newly_insured',
      waitFor: () =>
        freeSubAvailable !== undefined && userIsInsured !== undefined && newFeatureModalShown !== undefined,
      showIf: () => Boolean(userIsInsured && !newFeatureModalShown),
      onShow: (_: string) => {
        setNewFeatureModalShown(true);
        setTimeout(() => {
          if (isFeatureEnabled(FeatureFlag.useNewOnboarding)) {
            navigation.navigate('presentCommunity');
          } else {
            navigateToIntro(navigation, freeSubAvailable!, 'newly_insured');
          }
        }, 4000);
      },
      deps: [freeSubAvailable, navigation, newFeatureModalShown, userIsInsured],
    },
    {
      // Third popup: Custom (eg. Utile) popup
      name: 'custom',
      waitFor: () => Boolean(profile && customModalsShown && numAppStarts !== undefined),
      showIf: () => {
        if (pausedModals) {
          // This atom allows us to wait while processing a B2B activation, so we don't show this custom popup before the B2B activated popup
          return false;
        }
        if (presentB2BActivatedModal) {
          return false;
        }
        if (numAppStarts! >= 2) {
          const signalData = getSignalData(userPlan as ResponseWithSignals | undefined, Signal.CustomPopup) as
            { content: string } | undefined;
          if (signalData?.content && !customModalsShown!.includes(signalData.content)) {
            return signalData.content;
          }
        }
        return false;
      },
      onShow: (signaDataContent: string) => {
        setTimeout(() => {
          navigateToIntro(navigation, false, 'custom', signaDataContent);
        }, 4000);
        setCustomModalsShown([...customModalsShown!, signaDataContent]);
        createNotification({
          subscriberId: profile!.userId,
          type: NotificationTemplateIdentifier.generic,
          title: `introModal.custom.${signaDataContent}.title`,
          message: `introModal.custom.${signaDataContent}.body`,
          link: `/intro/introModal?use=custom&customTemplate=${signaDataContent}&freeSubAvailable=false&from=notif`,
        });
      },
      deps: [
        createNotification,
        customModalsShown,
        navigation,
        numAppStarts,
        pausedModals,
        presentB2BActivatedModal,
        profile,
        setCustomModalsShown,
        userPlan,
      ],
    },
    {
      // Prompt to choose sharer type
      name: 'choose_sharer_type',
      waitFor: () =>
        chooseSharerTypeModalShown !== undefined &&
        userInfo?.acceptedGuidelineDate !== undefined &&
        userSharerType !== undefined &&
        numAppStarts !== undefined,
      showIf: () =>
        Boolean(
          isFeatureEnabled(FeatureFlag.promptSharerType) &&
          !isWhiteLabel(WhiteLabel.Mutuo) &&
          !chooseSharerTypeModalShown &&
          !userSharerType &&
          userInfo?.acceptedGuidelineDate &&
          numAppStarts! >= 2 &&
          hasProfile,
        ),
      onShow: (_: string) => {
        setChooseSharerTypeModalShown(true);
        setTimeout(() => {
          navigation.navigate('onboarding', { screen: 'choose-sharer-type' }, { pop: true });
        }, 200);
      },
      deps: [navigation, numAppStarts, chooseSharerTypeModalShown, userSharerType, setChooseSharerTypeModalShown],
    },
  ];

  // This is orchestration bookkeeping only; it does not participate in rendering.
  const modalsTriggered = useRef<boolean[]>([]);

  const showModalMaybe = useEffectEvent((i: number) => {
    // Wait for local dismissal history before letting another startup modal take the slot.
    if (i > 0 && !confirmationStateLoaded) return;
    const modal = modals[i]!;
    if (!modal.waitFor() || modalsTriggered.current[i]) return;

    const anyModalTriggered = modalsTriggered.current.find(Boolean);
    const showModalParam: string | boolean = modal.showIf();
    const showModal = Boolean(showModalParam) && (modal.showEvenIfOtherModalsTriggered || !anyModalTriggered);

    console.log(`[useAppStartModals] showModal #${(i + 1).toFixed(0)} (${modal.name})?`, showModal);

    if (!showModal) {
      return;
    }
    modalsTriggered.current[i] = true;
    setAlreadyRedirected(true);
    modal.onShow(showModalParam as string);
  });

  const startupModalCount = modals.length;
  useEffect(() => {
    if (confirmationStateLoaded) {
      for (let i = 0; i < startupModalCount; i++) showModalMaybe(i);
    }
  }, [confirmationStateLoaded, startupModalCount]);

  useEffect(
    () => showModalMaybe(0),
    [
      userPlan,
      subscriptionPromptStarted,
      pendingConfirmation,
      codeConfirmationsShown,
      setCodeConfirmationsShown,
      setSelectedCodeConfirmation,
      setIsSubscriptionCodeModalOpen,
      hasProfile,
      isOnboarding,
    ],
  );
  useEffect(
    () => showModalMaybe(1),
    [
      isLoggedIn,
      isUserMetaModalOpen,
      setIsUserMetaModalOpen,
      setUserMetaModalShown,
      userMetaModalShown,
      userMetaPrompt,
    ],
  );
  useEffect(
    () => showModalMaybe(2),
    [
      freeSubAvailable,
      isLoggedIn,
      navigation,
      userPlan,
      userTrialModalPreference,
      showIntroAfterSubscriptionCodeModal,
      isSubscriptionCodeModalOpen,
      subscriptionPromptStarted,
      pendingConfirmation,
      codeConfirmationsShown,
      setCodeConfirmationsShown,
      setSelectedCodeConfirmation,
      hasProfile,
      isOnboarding,
      isUserMetaModalOpen,
      isBabillardReady,
    ],
  );
  useEffect(() => showModalMaybe(3), [navigation, presentB2CPaidModal, setPresentB2CPaidModal]);
  useEffect(() => showModalMaybe(4), [navigation, presentB2BActivatedModal]);
  useEffect(() => showModalMaybe(5), [freeSubAvailable, navigation, newFeatureModalShown, userIsInsured]);
  useEffect(
    () => showModalMaybe(6),
    [
      createNotification,
      customModalsShown,
      navigation,
      numAppStarts,
      pausedModals,
      presentB2BActivatedModal,
      profile,
      setCustomModalsShown,
      userPlan,
    ],
  );
  useEffect(
    () => showModalMaybe(7),
    [navigation, numAppStarts, chooseSharerTypeModalShown, userSharerType, setChooseSharerTypeModalShown],
  );

  // Announcements must wait for every startup decision, including delayed navigations.
  // An unresolved query/history is not evidence that there is nothing to show.
  return {
    canPresentFeature: canPresentFeatureAfterStartup(
      [confirmationStateLoaded, plansLoaded, profile],
      [
        freeSubAvailable,
        userIsInsured,
        userTrialModalPreference,
        numAppStarts,
        customModalsShown,
        newFeatureModalShown,
        b2BActivatedModalShown,
        chooseSharerTypeModalShown,
        userSharerType,
      ],
      [alreadyRedirected, pausedModals],
      modals,
    ),
  };
}
